/** Lightweight markdown helpers for previews and search (the editor uses TipTap). */

export type PreviewLine =
  | { kind: 'heading'; text: string; level: number; index: number }
  | { kind: 'task'; text: string; checked: boolean; index: number; indent: number }
  | { kind: 'bullet'; text: string; index: number; indent: number; marker: string }
  | { kind: 'text'; text: string; index: number };

const TASK = /^(\s*)[-*+]\s+\[([ xX])\]\s?(.*)$/;
const BULLET = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/;
const HEADING = /^(#{1,6})\s+(.*)$/;

/** Removes inline markdown syntax. */
export function stripInline(text: string): string {
  return text
    .replace(/\\([\\`*_{}[\]()#+\-.!~])/g, '$1')
    .replace(/(\*\*|__)(.+?)\1/g, '$2')
    .replace(/([*_])(.+?)\1/g, '$2')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1');
}

/** Classifies a single markdown line. */
export function parseLine(line: string, index: number): PreviewLine {
  const indent = Math.floor((/^\s*/.exec(line)?.[0].length ?? 0) / 2);
  const task = TASK.exec(line);
  if (task) return { kind: 'task', text: stripInline(task[3] ?? ''), checked: task[2] !== ' ', index, indent };
  const heading = HEADING.exec(line);
  if (heading) return { kind: 'heading', text: stripInline(heading[2] ?? ''), level: heading[1]?.length ?? 1, index };
  const bullet = BULLET.exec(line);
  if (bullet) {
    const marker = /\d/.test(bullet[2] ?? '') ? (bullet[2] ?? '') : '•';
    return { kind: 'bullet', text: stripInline(bullet[3] ?? ''), index, indent, marker };
  }
  return { kind: 'text', text: stripInline(line.trim()), index };
}

/** Parses markdown into simplified lines for note cards. */
export function previewLines(markdown: string, max = 12): PreviewLine[] {
  const lines: PreviewLine[] = [];
  const raw = markdown.split('\n');
  for (let index = 0; index < raw.length && lines.length < max; index++) {
    const line = raw[index] ?? '';
    if (line.trim() !== '') lines.push(parseLine(line, index));
  }
  return lines;
}

/** Converts markdown to plain text (for search and titles). */
export function toPlainText(markdown: string): string {
  return markdown
    .split('\n')
    .map((line) => {
      const task = TASK.exec(line);
      if (task) return task[3] ?? '';
      const heading = HEADING.exec(line);
      if (heading) return heading[2] ?? '';
      const bullet = BULLET.exec(line);
      if (bullet) return bullet[3] ?? '';
      return line;
    })
    .map(stripInline)
    .join('\n')
    .trim();
}

/** Toggles the checkbox of the task on the given line index. */
export function toggleTaskLine(markdown: string, lineIndex: number): string {
  const lines = markdown.split('\n');
  const line = lines[lineIndex];
  if (line === undefined) return markdown;
  const m = TASK.exec(line);
  if (!m) return markdown;
  lines[lineIndex] = line.replace(/\[([ xX])\]/, m[2] === ' ' ? '[x]' : '[ ]');
  return lines.join('\n');
}

/** Counts open and done tasks. */
export function taskStats(markdown: string): { done: number; total: number } {
  let done = 0;
  let total = 0;
  for (const line of markdown.split('\n')) {
    const m = TASK.exec(line);
    if (!m) continue;
    total++;
    if (m[2] !== ' ') done++;
  }
  return { done, total };
}
